// src/ReferralShare.jsx
// In-app gift & referral panel for subscribed+verified users.
// Gift: $10 upfront, recipient gets 1 free month.
// Referral: free share link, recipient gets 7-day trial.
// Shows user's own stats and leaderboard position.

import { useState, useEffect } from 'react';
import { API_URL } from './apiConfig';

const APP_URL = 'https://spark.narbodica.com.au';

export default function ReferralShare({ user, subscription }) {
  const [tab, setTab]         = useState('share'); // share | history | leaderboard
  const [loading, setLoading] = useState(false);
  const [myStats, setMyStats] = useState(null);
  const [leaderboard, setBoard] = useState([]);
  const [referralLink, setRefLink] = useState('');
  const [copied, setCopied]   = useState(false);
  const [giftEmail, setGiftEmail] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [giftStep, setGiftStep] = useState('form'); // form | confirm | done
  const [giftResult, setGiftResult] = useState(null);
  const [error, setError]     = useState('');

  const token = localStorage.getItem('accessToken');
  const h = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const canShare = subscription?.isValid && user?.isIdentityVerified;

  useEffect(() => {
    loadMyStats();
    loadLeaderboard();
  }, []);

  const loadMyStats = async () => {
    try {
      const r = await fetch(`${API_URL}/referral/mine`, { headers: h }).then(r => r.json());
      if (r.success) setMyStats(r.data);
    } catch {}
  };

  const loadLeaderboard = async () => {
    try {
      const r = await fetch(`${API_URL}/referral/leaderboard?limit=20`).then(r => r.json());
      if (r.success) setBoard(r.data);
    } catch {}
  };

  const getOrCreateReferralLink = async () => {
    if (referralLink) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/referral/link`, { method: 'POST', headers: h }).then(r => r.json());
      if (r.success) {
        setRefLink(r.data.claimUrl);
        await loadMyStats();
      } else setError(r.message || 'Failed to create link');
    } catch { setError('Network error'); }
    setLoading(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {}
  };

  const sendGift = async () => {
    if (!giftEmail.trim()) return setError('Enter the recipient\'s email');
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/referral/gift`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ recipientEmail: giftEmail, message: giftMessage }),
      }).then(r => r.json());

      if (r.success) {
        setGiftResult(r.data);
        setGiftStep('done');
        await loadMyStats();
      } else setError(r.message || 'Failed to send gift');
    } catch { setError('Network error'); }
    setLoading(false);
  };

  // Styles
  const T = {
    bg: 'var(--bg-primary,#09090b)',
    surface: 'var(--bg-secondary,#18181b)',
    border: 'var(--border,#27272a)',
    text: 'var(--text-primary,#f4f4f5)',
    muted: 'var(--text-tertiary,#71717a)',
    dim: 'var(--text-secondary,#a1a1aa)',
    accent: '#7c3aed',
    gold: '#f59e0b',
  };

  const tabStyle = (active) => ({
    flex: 1, padding: '10px', border: 'none', cursor: 'pointer',
    background: active ? T.accent : 'transparent',
    color: active ? '#fff' : T.muted,
    fontSize: 13, fontWeight: active ? 600 : 400,
    borderRadius: 8, transition: 'all 0.15s',
  });

  const inputStyle = {
    width: '100%', padding: '10px 12px', background: T.bg,
    border: `1px solid ${T.border}`, borderRadius: 10, color: T.text,
    fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
  };

  const btnStyle = (color = T.accent) => ({
    width: '100%', padding: '12px', border: 'none', borderRadius: 10,
    background: color, color: '#fff', fontSize: 14, fontWeight: 600,
    cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1,
    fontFamily: 'inherit',
  });

  if (!canShare) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: T.muted }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 14, color: T.dim, lineHeight: 1.6 }}>
          {!subscription?.isValid
            ? 'You need an active subscription to send gifts and referrals.'
            : 'Identity verification is required before sending gifts or referrals. Verify your identity in Settings.'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", color: T.text, maxWidth: 480, margin: '0 auto' }}>

      {/* Stats bar */}
      {myStats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 20 }}>
          {[
            ['Gifts', myStats.stats.giftsSent, T.gold],
            ['Referrals', myStats.stats.referralsSent, '#60a5fa'],
            ['Claimed', myStats.stats.totalClaimed, '#a78bfa'],
            ['Converted', myStats.stats.totalConverted, '#34d399'],
          ].map(([l, n, c]) => (
            <div key={l} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: c }}>{n}</div>
              <div style={{ fontSize: 10, color: T.muted, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 6, padding: '4px', background: T.surface, borderRadius: 12, marginBottom: 20, border: `1px solid ${T.border}` }}>
        <button style={tabStyle(tab === 'share')} onClick={() => setTab('share')}>Share</button>
        <button style={tabStyle(tab === 'history')} onClick={() => { setTab('history'); }}>History</button>
        <button style={tabStyle(tab === 'leaderboard')} onClick={() => setTab('leaderboard')}>🏆 Board</button>
      </div>

      {/* ── SHARE TAB ── */}
      {tab === 'share' && (
        <div style={{ display: 'grid', gap: 16 }}>

          {/* Referral link card */}
          <div style={{ background: T.surface, border: `1px solid #3730a330`, borderRadius: 14, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ background: 'rgba(96,165,250,0.15)', borderRadius: 8, padding: 8, fontSize: 18 }}>🔗</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Refer a friend</div>
                <div style={{ fontSize: 12, color: T.muted }}>They get a 7-day free trial · Free for you · Tracked to you</div>
              </div>
            </div>

            {!referralLink ? (
              <button onClick={getOrCreateReferralLink} disabled={loading} style={{ ...btnStyle('#1d4ed8'), background: '#1d4ed8' }}>
                {loading ? 'Creating link...' : 'Generate my referral link'}
              </button>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input readOnly value={referralLink} style={{ ...inputStyle, fontSize: 12, color: T.muted, flex: 1 }} onClick={e => e.target.select()} />
                  <button onClick={copyLink}
                    style={{ padding: '10px 16px', background: copied ? '#15803d' : T.accent, border: 'none', borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <button onClick={() => { const text = `I've been using Narbodica — genuinely good apps. Join me: ${referralLink}`; if (navigator.share) { navigator.share({ title: 'Join Narbodica', text, url: referralLink }); } else { window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank'); } }}
                    style={{ flex: 1, padding: '9px', background: '#15803d', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Share via WhatsApp
                  </button>
                  <button onClick={() => { const t = encodeURIComponent(`Join me on Narbodica: ${referralLink}`); window.open(`sms:?&body=${t}`, '_blank'); }}
                    style={{ flex: 1, padding: '9px', background: '#1d4ed8', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    Send via SMS
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Gift card */}
          <div style={{ background: T.surface, border: `1px solid ${T.gold}30`, borderRadius: 14, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <div style={{ background: 'rgba(245,158,11,0.15)', borderRadius: 8, padding: 8, fontSize: 18 }}>🎁</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Gift a month</div>
                <div style={{ fontSize: 12, color: T.muted }}>You pay $10 · They get 1 month free · No commitment for them</div>
              </div>
            </div>

            {giftStep === 'form' && (
              <div style={{ display: 'grid', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 5 }}>Recipient's email *</label>
                  <input value={giftEmail} onChange={e => setGiftEmail(e.target.value)} type="email"
                    placeholder="friend@email.com" style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: 5 }}>Personal message (optional)</label>
                  <textarea value={giftMessage} onChange={e => setGiftMessage(e.target.value)}
                    placeholder="I think you'd love this..." rows={2}
                    style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
                {error && <div style={{ fontSize: 12, color: '#ef4444', padding: '8px 10px', background: 'rgba(239,68,68,0.08)', borderRadius: 8 }}>{error}</div>}
                <button onClick={() => { if (giftEmail.trim()) setGiftStep('confirm'); else setError('Enter recipient email'); }}
                  style={{ ...btnStyle(T.gold), background: T.gold }}>
                  Continue — Review $10 charge
                </button>
              </div>
            )}

            {giftStep === 'confirm' && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ padding: '12px 14px', background: T.bg, borderRadius: 10, border: `1px solid ${T.border}`, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: T.muted }}>Gift for</span>
                    <span style={{ fontWeight: 600 }}>{giftEmail}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: T.muted }}>They get</span>
                    <span style={{ fontWeight: 600 }}>1 month free access</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 4 }}>
                    <span style={{ fontWeight: 700 }}>You pay</span>
                    <span style={{ fontWeight: 700, color: T.gold }}>$10 AUD</span>
                  </div>
                </div>
                {giftMessage && (
                  <div style={{ padding: '10px 12px', background: 'rgba(167,139,250,0.08)', borderRadius: 8, fontSize: 12, fontStyle: 'italic', color: '#c7d2fe' }}>
                    "{giftMessage}"
                  </div>
                )}
                {error && <div style={{ fontSize: 12, color: '#ef4444', padding: '8px 10px', background: 'rgba(239,68,68,0.08)', borderRadius: 8 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setGiftStep('form'); setError(''); }} style={{ flex: 1, padding: '11px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 10, color: T.muted, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13 }}>
                    Back
                  </button>
                  <button onClick={sendGift} disabled={loading} style={{ flex: 2, ...btnStyle(T.gold), background: T.gold }}>
                    {loading ? 'Processing...' : 'Send Gift — $10'}
                  </button>
                </div>
                <p style={{ fontSize: 11, color: T.muted, textAlign: 'center', margin: 0 }}>
                  Payment will be charged to your saved card. They'll receive an email with a claim code.
                </p>
              </div>
            )}

            {giftStep === 'done' && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🎉</div>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Gift sent!</div>
                <div style={{ fontSize: 13, color: T.dim, lineHeight: 1.6, marginBottom: 16 }}>
                  {giftEmail} will receive an email with their claim code. Once they sign up, it shows up in your history.
                </div>
                <button onClick={() => { setGiftStep('form'); setGiftEmail(''); setGiftMessage(''); setError(''); }}
                  style={{ ...btnStyle(), width: 'auto', padding: '10px 24px' }}>
                  Send another gift
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === 'history' && (
        <div>
          {!myStats?.invites?.length ? (
            <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📭</div>
              No gifts or referrals sent yet.
            </div>
          ) : myStats.invites.map(inv => {
            const statusColors = { pending:'#71717a', claimed:'#a78bfa', converted:'#34d399', expired:'#52525b', payment_failed:'#ef4444' };
            const col = statusColors[inv.status] || '#71717a';
            return (
              <div key={inv._id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: inv.type === 'gift' ? T.gold : '#60a5fa' }}>
                      {inv.type === 'gift' ? '🎁 Gift' : '🔗 Referral'}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: col, background: col + '15', padding: '1px 6px', borderRadius: 10 }}>
                      {inv.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: T.dim }}>
                    {inv.recipientName || inv.recipientEmail || 'Unclaimed'}
                  </div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                    Sent {new Date(inv.createdAt).toLocaleDateString('en-AU', { day:'numeric', month:'short', year:'numeric' })}
                    {inv.claimedAt && ` · Claimed ${new Date(inv.claimedAt).toLocaleDateString('en-AU', { day:'numeric', month:'short' })}`}
                    {inv.convertedAt && ` · Converted ⭐`}
                  </div>
                </div>
                {inv.status === 'pending' && !inv.recipientEmail && (
                  <div style={{ fontFamily: 'monospace', fontSize: 13, letterSpacing: 2, color: '#a78bfa', background: 'rgba(167,139,250,0.1)', padding: '6px 10px', borderRadius: 8 }}>
                    {inv.code}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── LEADERBOARD TAB ── */}
      {tab === 'leaderboard' && (
        <div>
          <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, marginBottom: 14, fontSize: 12, color: T.dim, lineHeight: 1.6 }}>
            👀 <strong style={{ color: T.gold }}>Coming soon:</strong> Once Narbodica is profitable, top referrers and gifters will share in the success. Track your position now.
          </div>
          {!leaderboard.length ? (
            <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>No data yet — be the first on the board!</div>
          ) : leaderboard.map((row, i) => {
            const isMe = row._id === user?._id;
            return (
              <div key={row._id}
                style={{ background: isMe ? 'rgba(124,58,237,0.08)' : T.surface, border: `1px solid ${isMe ? '#7c3aed40' : T.border}`, borderRadius: 12, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ fontSize: 20, minWidth: 28, textAlign: 'center' }}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i+1}`}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{row.referrerName}{isMe ? ' (you)' : ''}</div>
                  <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>
                    🎁 {row.totalGifts} gifts · 🔗 {row.totalReferrals} referrals · ⭐ {row.totalConverted} converted
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: row.conversionRate >= 50 ? '#34d399' : '#a78bfa' }}>{row.conversionRate}%</div>
                  <div style={{ fontSize: 10, color: T.muted }}>conversion</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
